import React, { useCallback, useMemo } from 'react';
import { DashboardHeader } from "features/dashboard/dashboard-header/DashboardHeader";
import { SimpleDashboardContent } from "features/dashboard/DashboardContent";
import { Column } from "components/display/Table/types";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import GenericBadge from 'components/ui/Badge';
import BadgeTooltip from 'components/ui/BadgeTooltip';
import { DropdownButton, useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { ContractIdentifier, ContractRow, ContractType } from '../types/interfaces';
import { ContractTableRowKey } from '../types/enums';
import { Status } from 'types/optim/status';
import { useDialog } from 'hooks/useDialog';
import { Sizes } from 'components/forms/NumberSelector';
import SelectBrokerBranches from 'features/dashboard/dashboard-menu/SelectBrokerBranches';
import { useNavigate } from 'react-router-dom';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import {
  FacetQueryCommand,
  INCLUDE_TESTS_BOOLEAN_FACET,
  mapFacetFiltersToFiltersDto
} from 'shared/contexts/table-context/facetFilters';
import { buildExportCommand, exportListCsv } from 'shared/contexts/table-context/exportCsv';
import { useSharedViewDetection } from 'shared/contexts/table-context/useSharedViewDetection';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';
import { ScrollText, Trash2 } from 'lucide-react';
import { GenericViewHeader } from 'back-office/shared/components/GenericViewHeader';
import { ContractFetchParams } from 'slices/generic-table/hooks/useContractTable';
import { mapContractSummaries } from '../mappers/mapContractSummaries';
import { GetGenericListCommandDto, GetSummaryContractListResponse } from 'api/contract';
import { contractsAPI } from 'services/swagger-api';
import { TableResponse } from 'slices/generic-table/genericTableSlice';
import { useDocumentViewerDialog } from 'hooks/useDocumentViewerDialog';
import { DocumentScopeEnum } from 'api/documents';
import { formatDay } from 'core/utils/dates';


interface ContractViewProps {
  hideCreate?: boolean;
  /** Liste admin BO (toutes plateformes) : routes /admin/liste + colonnes Courtier/Plateforme. */
  admin?: boolean;
}

/** Dates CALENDAIRES (date d'effet, fin de contrat) : découpe de string, zéro fuseau. */
const fmtDate = (iso?: string): string => (iso ? formatDay(iso) : '—');

// Colonnes masquées par défaut côté courtier (évite le scroll ; réactivables via « Colonnes »)
const COURTIER_HIDDEN_COLUMNS = ['tunnelLabel', 'support', 'effectiveDate', 'endDate', 'mainDueDate'];

const ContractView: React.FC<ContractViewProps> = ({ hideCreate = false, admin = false }) => {
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Contract, NsKey.Risk, NsKey.Pricing]);
  // Vue partagée (rôle Plateforme / portefeuille partagé) : colonne Courtier si le back
  // remplit brokerName sur les lignes (détecteur par données).
  const { isSharedView, detectFromItems } = useSharedViewDetection();
  // Droits pilotés par le rôle actif (manager central), au niveau ACTION :
  //  - clic sur le numéro + œil (consultation) => tout le monde (colonne conservée) ;
  //  - menu d'écriture + créer + suppression groupée => Courtier uniquement.
  const canOpenDetails = useRoleCan(Capability.ContractOpen);
  const canViewActions = useRoleCan(Capability.ContractViewActions);
  const canWriteActions = useRoleCan(Capability.ContractWriteActions);
  const canSeeNotifBadges = useRoleCan(Capability.ListNotificationBadges);
  const { createMenuItem } = useActionMenu();
  const { openBlankDialog } = useDialog()
  const navigate = useNavigate()
  const { openDocumentViewer } = useDocumentViewerDialog();

  const onNewContract = useCallback(() => {
    openBlankDialog({
      size: Sizes['5xl']
    }, <SelectBrokerBranches />)
  }, [openBlankDialog]);

  const goToContractDetails = useCallback((contractId: string, type: ContractType) => {
    navigate(`/mes-contrats/details/${contractId}`, {
      state: { type }
    });
  }, [navigate]);

  const columns: Column<ContractRow>[] = useMemo(() => [
    {
      key: ContractTableRowKey.DATE,
      label: t('COMMON.DATE'),
      sortable: true,
      sortKey: 'date',
      render: (value) => {
        const date = new Date(value as string);
        const day = date.getDate();
        const month = date.toLocaleDateString('fr-FR', { month: 'short' });
        const year = date.getFullYear();
        const time = date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        });

        return (
          <div>
            <div className="text-sm font-medium">{day} {month} {year}</div>
            <div className="text-xs text-gray-500">{time}</div>
          </div>
        );
      }
    },
    {
      key: ContractTableRowKey.CONTRACT_IDENTIFIER,
      label: t('CONTRACT.LABELS.CONTRACT_NUMBER'),
      sortKey: 'identifier',
      render: (value, row) => {
        const contractIdentifier = value as ContractIdentifier;
        // Pastille de notifications : Courtier uniquement (matrice centrale)
        const unreadCount = canSeeNotifBadges ? row[ContractTableRowKey.UNREAD_NOTIFICATION_COUNT] || 0 : 0;

        if (!contractIdentifier.id) {
          return <></>;
        }

        return (
          <div
            onClick={
              canOpenDetails
                ? () => goToContractDetails(contractIdentifier.id, contractIdentifier.type)
                : undefined
            }
            className={
              canOpenDetails
                ? 'cursor-pointer inline-flex items-center gap-2'
                : 'inline-flex items-center gap-2'
            }
          >
            <span
              className={
                canOpenDetails
                  ? 'font-bold text-primary hover:underline'
                  : 'font-bold text-gray-700'
              }
            >
              {contractIdentifier.label}
            </span>
            {unreadCount > 0 && (
              <BadgeTooltip content={`${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`}>
                <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-secondary text-white text-[10px] font-semibold rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              </BadgeTooltip>
            )}
          </div>
        );
      }
    },
    {
      key: ContractTableRowKey.INSUREDS,
      label: t('COMMON.LABELS.INSURED_PERSONS'),
      sortKey: 'insured',
    },
    {
      // Affiche le libellé lisible (productLabel) avec le code en sous-titre.
      // Le tri suit la colonne affichée : productLabel (nom), pas product (code).
      key: ContractTableRowKey.PRODUCT,
      label: t('COMMON.LABELS.PRODUCT'),
      sortKey: 'productLabel',
      render: (value, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {row.productLabel || (value as string)}
          </div>
          {row.productLabel && (
            <div className="text-xs text-gray-500">{value as string}</div>
          )}
        </div>
      ),
    },
    {
      // tunnelLabel se trie via la clé back "tunnel" (même valeur)
      key: 'tunnelLabel',
      label: 'Tunnel',
      sortKey: 'tunnel',
      render: (value) => <span className="text-sm text-gray-700">{(value as string) || '—'}</span>,
    },
    {
      key: ContractTableRowKey.SUPPORT,
      label: t('COMMON.LABELS.SUPPORT'),
    },
    {
      key: 'effectiveDate',
      label: "Date d'effet",
      sortKey: 'effectiveDate',
      render: (value) => <span className="text-sm text-gray-700">{fmtDate(value as string)}</span>,
    },
    {
      key: 'endDate',
      label: 'Fin de contrat',
      sortKey: 'endDate',
      render: (value) => <span className="text-sm text-gray-700">{fmtDate(value as string)}</span>,
    },
    {
      // Format "jj/MM" renvoyé tel quel par le back (tri chronologique côté serveur)
      key: 'mainDueDate',
      label: 'Échéance principale',
      sortKey: 'mainDueDate',
      render: (value) => <span className="text-sm text-gray-700">{(value as string) || '—'}</span>,
    },
    {
      key: ContractTableRowKey.OWNER,
      label: t('COMMON.LABELS.INSURER'),
    },
    // Colonne Courtier : admin (routes /admin/liste) OU vue partagée (brokerName rempli).
    // Le tri courtier n'est garanti qu'en admin -> pas de sortKey hors admin.
    ...(admin || isSharedView
      ? ([
          {
            key: 'brokerName',
            label: 'Courtier',
            ...(admin ? { sortKey: 'broker' } : {})
          }
        ] as Column<ContractRow>[])
      : []),
    // Colonne Plateforme : admin uniquement.
    ...(admin
      ? ([
          {
            key: 'plateformeName',
            label: 'Plateforme',
            sortKey: 'plateforme'
          }
        ] as Column<ContractRow>[])
      : []),
    {
      key: ContractTableRowKey.CLIENT_PRICE,
      label: t('PRICING.LABELS.CLIENT_PRICE'),
      sortKey: 'clientPrice',
    },
    {
      key: ContractTableRowKey.STATUS,
      label: t('COMMON.STATUS'),
      sortable: true,
      sortKey: 'status',
      render: (value) => {
        const status = value as Status
        return (
          <GenericBadge
            label={status.label}
            color={status.color}
            badgeType="sm-bold"
          />
        )
      }
    }
  ], [t, goToContractDetails, admin, isSharedView, canOpenDetails, canSeeNotifBadges]);

  const getMoreMenuActions = useCallback((row: ContractRow) => [
    {
      options: {
        icon: 'fileText',
      },
      key: 'viewDocuments',
      label: t("CONTRACT.ACTIONS.VIEW_DOCUMENTS"),
      action: () => {
        openDocumentViewer({
          strategy: DocumentScopeEnum.Contract,
          entityId: row.contractIdentifier.id
        });
      }
    },
    {
      options: {
        icon: 'arrowRightLeft',
      },
      key: 'terminateContract',
      label: t("CONTRACT.ACTIONS.TERMINATE_CONTRACT"),
      action: () => { }
    },
    {
      options: {
        icon: 'arrowRightLeft',
      },
      key: 'issueCertificate',
      label: t("CONTRACT.ACTIONS.ISSUE_CERTIFICATE"),
      action: () => { }
    }
  ], [t, openDocumentViewer]);

  const getMenuItems = useCallback((row: ContractRow) => {
    return getMoreMenuActions(row).map(action =>
      createMenuItem(
        action.key,
        action.label,
        action.action,
        action.options
      )
    );
  }, [getMoreMenuActions, createMenuItem]);

  const actions = useMemo(() => ({
    // Créer / suppression groupée : écriture => Courtier uniquement.
    ...(hideCreate || !canWriteActions ? {} : {
      create: {
        label: t("FEATURES.ACTIONS.NEW_CONTRACT"),
        onClick: onNewContract
      }
    }),
    ...(canWriteActions ? {
      bulk: [
        {
          label: t("COMMON.DELETE"),
          icon: <Trash2 size={16} />,
          variant: "danger" as const,
          onClick: (rows: ContractRow[]) => {
            console.log("Delete simulations:", rows.length);
          }
        }
      ]
    } : {}),
    // Colonne d'actions au niveau action : œil (consultation) pour tous, menu pour Courtier.
    row: [
      ...(canViewActions ? [{
        label: 'seeMore',
        render: (row: ContractRow, index: number) => <Button key={index} color='slate' round>
          <EyeIcon />
        </Button>
      }] : []),
      ...(canWriteActions ? [{
        label: 'moreMenu',
        render: (row: ContractRow, index: number) => <DropdownButton key={index} width='w-64' anchor='bottom' items={getMenuItems(row)} />
      }] : [])
    ]
  }), [t, getMenuItems, onNewContract, hideCreate, canViewActions, canWriteActions]);

  const fetchContracts = useCallback(async (params: ContractFetchParams): Promise<TableResponse<ContractRow>> => {
    try {
      const command: GetGenericListCommandDto = {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        searchString: params.searchString || undefined,
        // Tri serveur par colonne (omis -> tri par défaut du back)
        sortBy: (params as any).sortBy,
        sortDirection: (params as any).sortDirection,
        // Filtres du panneau à facettes (indexés par facet.key) -> FiltersDto
        filters: mapFacetFiltersToFiltersDto((params as any).filters)
      };

      const result = admin
        ? await contractsAPI.getAdminContracts({ getGenericListCommandDto: command as any })
        : await contractsAPI.getSummaryContracts({ getGenericListCommandDto: command });

      const items = (result.items || []) as any[];
      let mappedContracts = mapContractSummaries(items);

      if (admin) {
        // La liste admin porte en plus les dimensions courtier / plateforme
        mappedContracts = mappedContracts.map((row, index) => ({
          ...row,
          brokerName: items[index]?.brokerName,
          plateformeName: items[index]?.plateformeName
        }));
      } else {
        // Vue partagée : brokerName rempli par le back sur les routes courtier.
        mappedContracts = mappedContracts.map((row, index) => ({
          ...row,
          brokerName: items[index]?.brokerName
        }));
        detectFromItems(items);
      }

      // Appliquer les filtres côté client si nécessaire
      let filteredContracts = mappedContracts;

      if (params.dateRange) {
        filteredContracts = filteredContracts.filter(sim => {
          const simDate = new Date(sim.date);
          return simDate >= params.dateRange!.start && simDate <= params.dateRange!.end;
        });
      }

      return {
        items: filteredContracts,
        totalItems: result.totalCount || filteredContracts.length,
        totalPages: Math.ceil((result.totalCount || filteredContracts.length) / params.pageSize),
        currentPage: params.pageNumber
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des quotes:', error);
      throw error;
    }
  }, [admin, detectFromItems])

  const fetchContractFacets = useCallback(
    (command: FacetQueryCommand) =>
      admin
        ? contractsAPI.getAdminContractListFacets({ getGenericListCommandDto: command })
        : contractsAPI.getContractListFacets({ getGenericListCommandDto: command }),
    [admin]
  );

  return (
    <SimpleDashboardContent>
      {/* Barre de recherche globale : espace courtier uniquement, pas en admin BO */}
      {!admin && <DashboardHeader />}
      {/* Identité de page admin (le titre du tableau est porté par ce header) */}
      {admin && <GenericViewHeader title="Contrats" icon={ScrollText} section="Activité" />}
      <ServerTable<ContractRow>
        apiConfig={{
          fetchData: fetchContracts,
        }}
        columns={columns}
        title={admin ? undefined : {
          iconKey: 'receiptText',
          label: t("FEATURES.TITLE.MY_CONTRACTS")
        }}
        actions={actions.row.length > 0 ? actions : undefined}
        selectable
        serverSorting
        centerColumns
        columnCustomization={{
          storageKey: admin ? 'bo-contrats' : 'courtier-contrats',
          defaultHidden: admin ? undefined : COURTIER_HIDDEN_COLUMNS
        }}
        facets={{ fetchFacets: fetchContractFacets, booleanFacets: INCLUDE_TESTS_BOOLEAN_FACET }}
        // Export CSV (admin) : rejoue la commande courante sur la route .../csv
        onExport={
          admin
            ? (p) =>
                exportListCsv(
                  '/api/backoffice/contrats/admin/liste/csv',
                  buildExportCommand(p, mapFacetFiltersToFiltersDto(p.filters))
                )
            : undefined
        }
        onRowClick={(row) => console.log("Contract clicked:", row.contractIdentifier)}
      />
    </SimpleDashboardContent>
  );
};

export default ContractView
